<script setup>
import { Swiper, SwiperSlide } from "swiper/vue";
import SwiperCore, { Navigation } from "swiper";
import { ref, watch } from "vue";
import { useElementVisibility } from "@vueuse/core";
import { trackSwiperEvents } from "@/analyticsHelper";

SwiperCore.use([Navigation]);

const testimonialData = ref([]);
const swiperComponent = ref(null);
const targetVisible = useElementVisibility(swiperComponent);
const triggeredOnce = ref(false);
const video = ref(null);

const onSlideChange = (swiper) => {
    if (video.value) {
        video.value.forEach((vid) => {
            if (swiper.activeIndex === video.value.indexOf(vid)) {
                vid.controls = true;
                vid.play();
                return;
            }
            vid.controls = false;
            vid.pause();
        });
    }
};

const unwatchSwiper = watch(targetVisible, (newValue) => {
    if (newValue) triggeredOnce.value = true;
    unwatchSwiper();
});

const unwatchVideos = watch(video, (newValue) => {
    newValue[0].play();
    unwatchVideos();
});

/**
 * Attempts to get the MIME type from a resource URL
 * @example getFileMimeTypeFromUrl("https://someurl.com/resource/video.mp4") => "video"
 *
 * @param resourceUrl
 * @returns {Promise<unknown>}
 */
const getFileMimeTypeFromUrl = (resourceUrl) =>
    new Promise((resolve) => {
        const fileExtension = resourceUrl.split(".").reverse()[0];

        // Try to guess it from the file extension to preserve API calls
        // eslint-disable-next-line default-case
        switch (fileExtension) {
            case "mp4":
            case "mov":
                resolve("video");
                return;

            case "jpg":
            case "jpeg":
            case "png":
                resolve("image");
                return;
        }

        // Can't guess it, get the MIME from the HEAD
        fetch(resourceUrl, { method: "HEAD" }).then((response) => {
            if (!response.headers.has("content-type")) {
                resolve(null);
            }

            resolve(response.headers.get("content-type").split("/")[0]);
        });
    });

fetch("/api/collections/testimonial/entries").then((result) => {
    result.json().then((responseData) => {
        Promise.all(
            responseData.data.map(async (testimonial) => {
                const permalink = testimonial.image_video?.permalink;

                return {
                    ...testimonial,
                    mime_type: permalink ? await getFileMimeTypeFromUrl(permalink) : null,
                };
            })
        ).then((mappedTestimonials) => {
            testimonialData.value = mappedTestimonials;
        });
    });
});
</script>

<template>
    <div ref="swiperComponent" class="swiper">
        <swiper
            v-if="targetVisible || triggeredOnce"
            :loop="true"
            :slides-per-view="1"
            :centered-slides="true"
            :allow-touch-move="true"
            :navigation="{
                prevEl: '.slider-prev',
                nextEl: '.slider-next',
            }"
            :breakpoints="{
                '768': {
                    slidesPerView: 2,
                },
                '1024': {
                    slidesPerView: 3,
                },
            }"
            class="w-full"
            data-slide-name="HOMEPAGE_TESTIMONIALS"
            @slide-change="onSlideChange"
            @swiper="trackSwiperEvents"
        >
            <swiper-slide
                v-for="(testimonial, testimonialIndex) in testimonialData"
                :key="testimonialIndex"
                v-slot="{ isActive }"
            >
                <div>
                    <div class="w-full" :class="testimonial.mime_type === 'video' ? 'aspect-video' : 'aspect-square'">
                        <div
                            class="w-full h-full overflow-hidden relative"
                            :class="isActive ? 'scale-100' : 'scale-90'"
                        >
                            <div
                                v-if="isActive && testimonial.mime_type === 'image'"
                                class="absolute z-10 w-full h-full flex flex-col justify-end p-10"
                            >
                                <h3
                                    class="font-heading text-xl md:text-2xl lg:text-3xl text-white mb-5 uppercase"
                                    v-text="testimonial.name"
                                ></h3>
                                <p class="prose text-white text-left font-semibold" v-text="testimonial.text"></p>
                            </div>

                            <img
                                v-if="testimonial.image_video && testimonial.mime_type === 'image'"
                                class="w-full h-full object-cover"
                                :src="testimonial.image_video.permalink"
                                :alt="testimonial.title"
                            />
                            <video
                                v-else-if="testimonial.image_video && testimonial.mime_type === 'video'"
                                ref="video"
                                class="w-full h-full object-cover"
                                muted
                                loop
                                playsinline
                            >
                                <source :src="testimonial.image_video.permalink" type="video/mp4" />
                            </video>
                        </div>
                    </div>
                </div>
            </swiper-slide>
            <div class="flex justify-center pt-5 space-x-3">
                <button class="slider-prev p-3 hover:text-orange">
                    <i class="fa-regular fa-long-arrow-left text-2xl"></i>
                </button>
                <button class="slider-next p-3 hover:text-orange">
                    <i class="fa-regular fa-long-arrow-right text-2xl"></i>
                </button>
            </div>
        </swiper>
    </div>
</template>
